import { DatePipe } from '@angular/common';
import { Component, ElementRef, forwardRef, Input, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, UntypedFormControl, UntypedFormGroup, Validator } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';

import { BsDatepickerConfig, BsLocaleService } from 'ngx-bootstrap/datepicker';
import { PopoverDirective } from 'ngx-bootstrap/popover';

import { I18nService } from '../../../../../i18n';
import { L10nService } from '../../../../../l10n';
import { FormDateQuestion } from '../../../formquestion-date';

@Component({
    selector: 'form-date-input-bootstrap',
    templateUrl: './date-input.component.html',
    styleUrls: ['./date-input.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => FormDateInputBootstrapComponent),
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => FormDateInputBootstrapComponent),
            multi: true,
        },
    ],
})
export class FormDateInputBootstrapComponent implements ControlValueAccessor, Validator {
    @ViewChild('dp', { static: true }) datePicker: PopoverDirective;
    @ViewChild('dateInputEl', { static: true }) dateInputEl: ElementRef<HTMLInputElement>;
    @Input() question: FormDateQuestion;
    @Input() form: UntypedFormGroup;

    bsConfig: Partial<BsDatepickerConfig>;
    value: Date;
    dateMask = [/[1-2]/, /[0-9]/, /[0-9]/, /[0-9]/, '-', /[0-1]/, /[0-9]/, '-', /[0-3]/, /[0-9]/];
    dateError = false;

    constructor(
        //
        private i18nService: I18nService,
        protected datePipe: DatePipe,
        private l10nService: L10nService,
        private localeService: BsLocaleService,
        private translateService: TranslateService,
    ) {
        this.value = null;

        this.translateService.setTranslation(
            'fr',
            {
                sysToday: `Aujourd'hui`,
                sysClear: 'Effacer',
            },
            true,
        );
        this.translateService.setTranslation(
            'en',
            {
                sysToday: 'Today',
                sysClear: 'Clear',
            },
            true,
        );

        this.bsConfig = {
            // Date-only field: dateMask below only ever allows typing yyyy-MM-dd (10 chars), so a
            // time portion here is never entered by the user. Keeping one anyway made ngx-bootstrap's
            // parseDate/formatDate default the missing time to midnight and re-render it as "12:00:00 am"
            // in the input on blur.
            dateInputFormat: 'YYYY-MM-DD',
            showWeekNumbers: false,
            containerClass: 'theme-acceo',
            showTodayButton: true,
            showClearButton: true,
            todayPosition: 'left',
            customTodayClass: 'bg-light',
            todayButtonLabel: this.translateService.instant('sysToday'),
            clearButtonLabel: this.translateService.instant('sysClear'),
        };

        this.localeService.use(this.l10nService.localeId);
    }

    onDatePickerChange(value: Date) {
        this.convertValue(value);
        this.onTouched();

        setTimeout(() => {
            // let htmlElement: HTMLElement = this.el.nativeElement;
            // let inputElement = htmlElement.getElementsByTagName('input')[0];

            const evt = document.createEvent('HTMLEvents');

            evt.initEvent('change', false, true);
            if (this.question.onChange != null) {
                this.question.onChange(evt, this.question);
            }
            // inputElement.dispatchEvent(evt);
        }, 0);
    }

    onQuestionChange(event: Event) {
        if (this.question.onChange != null) {
            this.question.onChange(event, this.question);
        }
    }

    // Needed for Safari/WebKit: clicking an action button doesn't move focus off the input first, so the
    // native `change` (and the `bsValueChange` it triggers on the datepicker directive) never fires before
    // the click handler runs, and a manually typed date is silently lost. Parses the year/month/day
    // components explicitly and builds the Date via the local-time constructor (`new Date(y, m, d)`) rather
    // than `new Date(inputValue)`: the latter parses a bare `yyyy-MM-dd` string as UTC midnight per the
    // ECMAScript date-only grammar, which shifts the date back a day in any negative-UTC-offset timezone.
    onManualInput(event: Event) {
        const inputValue = (event.target as HTMLInputElement).value;
        if (String.isNullOrEmpty(inputValue) || inputValue.length < this.dateMask.length) {
            return;
        }
        const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(inputValue);
        if (match == null) {
            return;
        }
        const year = Number(match[1]);
        const month = Number(match[2]);
        const day = Number(match[3]);
        const parsedDate = new Date(year, month - 1, day);
        if (parsedDate.getFullYear() !== year || parsedDate.getMonth() !== month - 1 || parsedDate.getDate() !== day) {
            // Invalid calendar date (e.g. 2024-02-30 would roll over to March): reject rather than propagate a shifted date.
            return;
        }
        this.convertValue(parsedDate);
        this.onTouched();
    }

    onClearButtonClick() {
        this.onDatePickerChange(null);
        if (this.datePicker.isOpen) {
            this.datePicker.hide();
        }
    }

    onSearchButtonClick() {
        this.toggleVisibility();
    }

    toggleVisibility() {
        this.datePicker.show();
    }

    hideDatePicker() {
        if (this.datePicker.isOpen) {
            this.datePicker.hide();
        }
    }

    private convertValue(value: Date, propagateChange = true) {
        try {
            if (value != null && isNaN(value.getTime())) {
                // An Invalid Date reaches here from the calendar/blur path (e.g. bsDatepicker's own
                // parsing on native `change`) when the typed text doesn't resolve to a real date.
                // Assigning it to `this.value` would crash the template's `date` pipe
                // (InvalidPipeArgument), so route it through the same error handling as a thrown error.
                throw new Error('Invalid date');
            }
            if (value != null) {
                //this.value = this.datePipe.transform(value, 'yyyy-MM-dd');
                this.value = value;
            } else {
                this.value = null;
            }
            this.dateError = false;
        } catch (e) {
            this.value = null;
            this.dateError = true;
            // bsDatepicker's own input directive reacts to the same bsValueChange and writes its
            // own fallback text (the literal string "Invalid date") straight into the native input via
            // Renderer2, bypassing our `value="{{ value | date }}"` binding. That write can happen
            // before or after Angular re-renders our binding to the (correct, empty) `this.value`,
            // so clear the input ourselves on the next tick to reliably win that race.
            setTimeout(() => {
                if (this.dateInputEl != null) {
                    this.dateInputEl.nativeElement.value = '';
                }
            }, 0);
        }
        if (propagateChange) {
            this.propagateChange(value);
        }
    }

    private propagateChange = (_: any) => {
        /* Must be empty */
    };

    onTouched = () => {
        /* Must be empty */
    };

    writeValue(value: string) {
        // Fix a bug introduced in the ngx-boostrap. The value was overriden by the affectation of the bsConfig initialization.
        setTimeout(() => {
            if (String.isNullOrEmpty(value)) {
                value = null;
            }
            if (typeof value === 'string') {
                if (!String.isNullOrEmpty(value)) {
                    this.value = new Date(value);
                } else {
                    this.value = null;
                }
            } else {
                this.value = value;
            }
        }, 0);
    }

    registerOnChange(fn: any) {
        this.propagateChange = fn;
    }

    registerOnTouched(fn: any) {
        this.onTouched = fn;
    }

    /**
     * Form the question label with the i18n service.
     *
     * @param {string} label Label to format.
     * @returns {string} The formatted label.
     *
     * @memberof FormQuestionBaseComponent
     */
    formatLabel(label: string): string {
        return this.i18nService.formatLabel(label);
    }

    get componentId() {
        return this.question.idPrefix + '-' + this.question.key.toLowerCase();
    }

    validate(c: UntypedFormControl) {
        return !this.dateError ? null : { validDate: false };
    }
}
